import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Clock, MapPin, Phone, Wrench } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Layout } from '../components/layout/Layout';
import { SEO } from '../components/shared/SEO';
import { CTASection } from '../components/shared/CTASection';
import { SERVICES, SERVICE_AREAS, COMPANY_INFO } from '../data/mock';

export default function ServicesHub() {
  return (
    <Layout>
      <SEO
        title="Sprinkler Repair Services Allen TX | AAA Irrigation Service"
        description="Full-service irrigation repair in Allen, Plano, McKinney &amp; Frisco TX. Sprinkler heads, valves, backflow installation, controllers &amp; emergency repair. Call 469-751-3567."
        keywords="irrigation services Allen TX, sprinkler repair services Plano TX, backflow preventer installation McKinney TX, valve repair Frisco, controller repair North Texas, commercial irrigation DFW, emergency sprinkler repair Collin County"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 py-16 md:py-24 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-teal-500 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-500 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-teal-500/10 border border-teal-500/20 rounded-full px-4 py-1.5 mb-6">
              <Shield className="w-4 h-4 text-teal-400" />
              <a 
                href={COMPANY_INFO.licenseUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-teal-400 font-medium hover:text-teal-300 transition-colors"
              >
                TCEQ Licensed: {COMPANY_INFO.license}
              </a>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Professional Irrigation
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-cyan-400">
                Services
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 mb-8 leading-relaxed">
              From routine maintenance to emergency repairs, AAA Irrigation Service delivers expert sprinkler and irrigation solutions throughout North Texas.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4">
              <Button asChild size="lg" className="bg-teal-500 hover:bg-teal-600 text-white shadow-lg shadow-teal-500/30 hover:shadow-teal-500/50 transition-all duration-300">
                <a href={`tel:${COMPANY_INFO.phone}`}>
                  <Phone className="w-4 h-4 mr-2" />
                  Call {COMPANY_INFO.phoneFormatted}
                </a>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white transition-all duration-300">
                <Link to="/services/emergency-repair">
                  <Clock className="w-4 h-4 mr-2" />
                  Emergency Service
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Our Irrigation Services
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Click on any service below to learn more about how we can help with your specific irrigation needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((service, index) => (
              <Link key={service.id} to={`/services/${service.id}`} className="group">
                <Card className="h-full border-slate-200 hover:border-teal-300 hover:shadow-xl hover:shadow-teal-500/10 transition-all duration-300 overflow-hidden">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={service.heroImage}
                      alt={`${service.title} - AAA Irrigation Service in Allen, Plano, McKinney TX`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                      width="400"
                      height="192"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
                  </div>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xl text-slate-900 group-hover:text-teal-600 transition-colors duration-300">
                      {service.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-slate-600 mb-4">
                      {service.shortDescription}
                    </CardDescription>
                    <span className="inline-flex items-center text-teal-600 font-medium text-sm group-hover:gap-2 transition-all duration-300">
                      Learn More
                      <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-300" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Serving North Texas
            </h2>
            <p className="text-lg text-slate-600">
              Professional irrigation services throughout Collin County and surrounding areas.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            {SERVICE_AREAS.map((area) => (
              <div
                key={area}
                className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2 shadow-sm hover:shadow-md hover:border-teal-300 transition-all duration-300"
              >
                <MapPin className="w-4 h-4 text-teal-500" />
                <span className="text-slate-700 font-medium">{area}, TX</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection />
    </Layout>
  );
}
